import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import LandingHero from "../components/LandingHero";

import { LEGAL_VERSION } from "../lib/legal";

// (top of file also imports register from AuthContext etc.)

const FOREST = "#0D9488";
const FOREST_D = "#0F766E";
const INK = "#0F172A";
const INK_MUT = "#64748B";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Required checkboxes — the account cannot be created until both are true.
  const [agreed, setAgreed] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to the Privacy Policy and Terms of Service to continue.");
      return;
    }
    if (!ageOk) {
      toast.error("You must confirm you are 21 years of age or older.");
      return;
    }
    setSubmitting(true);
    try {
      await register(name, email, password, { accept_terms: true, age_confirmed: true, terms_version: LEGAL_VERSION });
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "h-12 rounded-lg bg-white border focus-visible:ring-0 focus-visible:border-[#0D9488] transition-colors text-slate-900 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:border-slate-700";
  const inputStyle = { borderColor: "#E2E8F0" };

  // Reuse LandingHero but override the form-title copy for signup
  const signupSettings = {
    ...(settings || {}),
    landing_form_title: "Create your account",
    landing_form_subtitle: "A private journal for peptides, meals, and body composition.",
  };

  return (
    <LandingHero settings={signupSettings}>
      <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="signup-form">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em]" style={{ color: INK_MUT }}>Name</Label>
          <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
            className={inputClass} style={inputStyle}
            placeholder="Your name" data-testid="signup-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em]" style={{ color: INK_MUT }}>Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className={inputClass} style={inputStyle}
            placeholder="you@example.com" data-testid="signup-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em]" style={{ color: INK_MUT }}>Password</Label>
          <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className={inputClass} style={inputStyle}
            placeholder="At least 6 characters" data-testid="signup-password" />
        </div>

        {/* Required age attestation — peptides/hormones are 21+ per our ToS §2. */}
        <label
          className="flex items-start gap-3 cursor-pointer select-none rounded-lg border px-3 py-2.5 transition-colors"
          style={{
            borderColor: ageOk ? FOREST : "#E2E8F0",
            background: ageOk ? "rgba(13,148,136,0.05)" : "#FFFFFF",
          }}
          data-testid="signup-age-row"
        >
          <input
            type="checkbox"
            required
            checked={ageOk}
            onChange={(e) => setAgeOk(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 cursor-pointer accent-[#0D9488]"
            data-testid="signup-age-checkbox"
            aria-label="Confirm 21 years of age or older"
          />
          <span className="text-sm leading-snug" style={{ color: INK }}>
            I confirm that I am <b>21 years of age or older</b>.
          </span>
        </label>

        {/* Required agreement — the submit button stays disabled until both checkboxes are checked. */}
        <label
          className="flex items-start gap-3 cursor-pointer select-none rounded-lg border px-3 py-2.5 transition-colors"
          style={{
            borderColor: agreed ? FOREST : "#E2E8F0",
            background: agreed ? "rgba(13,148,136,0.05)" : "#FFFFFF",
          }}
          data-testid="signup-terms-row"
        >
          <input
            type="checkbox"
            required
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 cursor-pointer accent-[#0D9488]"
            data-testid="signup-terms-checkbox"
            aria-label="Agree to Privacy Policy and Terms of Service"
          />
          <span className="text-sm leading-snug" style={{ color: INK }}>
            I agree to the{" "}
            <Link to="/privacy" target="_blank" rel="noopener" className="font-semibold underline" style={{ color: FOREST }} data-testid="signup-privacy-link">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms" target="_blank" rel="noopener" className="font-semibold underline" style={{ color: FOREST }} data-testid="signup-terms-link">
              Terms of Service
            </Link>
            . I understand this Service is a tracking tool and does not provide medical advice.
          </span>
        </label>

        <Button type="submit" disabled={submitting || !agreed || !ageOk}
          className="w-full h-12 rounded-lg text-white uppercase tracking-widest text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: FOREST }}
          onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = FOREST_D; }}
          onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = FOREST; }}
          data-testid="signup-submit">
          {submitting ? "Creating…" : (<>Create account <ArrowRight className="w-4 h-4 ml-2" /></>)}
        </Button>
      </form>

      <div className="mt-6 pt-5 text-sm text-center" style={{ borderTop: "1px solid #E2E8F0", color: INK_MUT }}>
        Already have an account?{" "}
        <Link to="/login" className="hover:underline font-semibold" style={{ color: FOREST }} data-testid="link-login">
          Sign in
        </Link>
      </div>
    </LandingHero>
  );
}
